import { useEffect, useRef, useState } from 'react'
import { useVoiceBirthdayTrigger } from './useVoiceBirthdayTrigger'
import { runConfetti } from './voiceConfetti'

const DURATION = 4000

/**
 * Full-channel overlay that celebrates when a birthday person is present in the
 * voice channel. Renders a confetti/fireworks canvas + a fading "Happy Birthday"
 * banner. pointer-events:none — never blocks the voice controls. Honors
 * prefers-reduced-motion (banner only, no canvas animation).
 */
export function VoiceBirthdayCelebration() {
  const celebration = useVoiceBirthdayTrigger()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [names, setNames] = useState<string[] | null>(null)
  const [shown, setShown] = useState(false)

  // A new celebration → show the banner + schedule the fade-out and unmount.
  useEffect(() => {
    if (!celebration) return
    setNames(celebration.names)
    setShown(true)
    const fade = window.setTimeout(() => setShown(false), DURATION - 400)
    const clear = window.setTimeout(() => setNames(null), DURATION + 200)
    return () => { clearTimeout(fade); clearTimeout(clear) }
    // re-run only when a new celebration fires
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebration?.key])

  // Run the canvas animation once `names` is set — i.e. on the render where the
  // <canvas> is actually mounted (keying this on celebration.key instead would
  // fire one render too early, while the canvas ref is still null). `names` is a
  // fresh array per celebration and null in between, so this fires once each.
  useEffect(() => {
    if (!names) return
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce || !canvasRef.current) return
    return runConfetti(canvasRef.current, DURATION)
  }, [names])

  if (!names) return null
  const text =
    names.length === 1 ? names[0]
    : names.length === 2 ? `${names[0]} & ${names[1]}`
    : `${names[0]}, ${names[1]} +${names.length - 2}`

  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <div
        className={`absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-card/90 px-5 py-2 text-sm font-semibold text-foreground shadow-lg ring-1 ring-border backdrop-blur transition-opacity duration-500 ${shown ? 'opacity-100' : 'opacity-0'}`}
      >
        🎉 Happy Birthday, {text}!
      </div>
    </div>
  )
}

export default VoiceBirthdayCelebration
